import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import AddressAutocomplete from './common/AddressAutocomplete';
import localeConfig from '../config/locale.config';

const AddLocationRow = ({
    showAddLocationRow,
    setShowAddLocationRow,
    newLocation,
    loading,
    handleNewLocationChange,
    handleAddressChange,
    handleAddressSelect,
    handleOperatingHoursChange,
    handleAddLocation,
    formatMobileNumber
}) => {
    const isExpanded = showAddLocationRow;
    
    return (
        <div className="bg-white border border-gray-200 rounded-lg mb-4 overflow-hidden">
            {/* Main Row - Clickable */}
            <div 
                className="p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setShowAddLocationRow(!showAddLocationRow)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="text-gray-500">
                            {isExpanded ? (
                                <ChevronDown className="w-5 h-5" />
                            ) : (
                                <ChevronRight className="w-5 h-5" />
                            )}
                        </div>
                        
                        <div className="flex-1">
                            <div className="flex items-center space-x-3">
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Add New Location
                                </h3>
                                <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                    Registration Required
                                </span>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                                Click to expand and fill location details
                            </div>
                        </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                        <div className="text-sm text-gray-500">
                            Pending
                        </div>
                    </div>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div 
                    className="border-t border-gray-200 bg-gray-50 p-4"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-900 mb-2">Location Registration Form</h4>
                        <p className="text-xs text-gray-600">New locations require approval before being activated</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Location Type */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Location Type *
                            </label>
                            <select
                                value={newLocation.vendor_location_type}
                                onChange={(e) => handleNewLocationChange('vendor_location_type', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            >
                                <option value="VENDOR_ONLY">Vendor Only</option>
                                <option value="VENDOR_AND_DEALER">Vendor & Dealer</option>
                                <option value="DEALER_PARTNER">Dealer Partner</option>
                            </select>
                        </div>

                        {/* Address */}
                        <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Location Address *
                            </label>
                            <AddressAutocomplete
                                value={newLocation.location_address.address}
                                onChange={(value) => handleAddressChange(value)}
                                onAddressSelect={(addressData) => handleAddressSelect(addressData)}
                                placeholder="Search for location address"
                            />
                        </div>

                        {/* Manager Information Section */}
                        <div className="md:col-span-2">
                            <h4 className="text-sm font-medium text-gray-900 mb-2 border-b pb-1">Location Manager</h4>
                        </div>

                        {/* Manager Name */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Manager Name *
                            </label>
                            <input
                                type="text"
                                value={newLocation.manager_name}
                                onChange={(e) => handleNewLocationChange('manager_name', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter manager name"
                                required
                            />
                        </div>

                        {/* Manager Mobile */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Manager Mobile *
                            </label>
                            <input
                                type="tel"
                                value={newLocation.manager_mobile}
                                onChange={(e) => handleNewLocationChange('manager_mobile', formatMobileNumber(e.target.value))}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder={localeConfig.mobile.placeholder}
                                required
                            />
                        </div>

                        {/* Manager Email */}
                        <div>
                            <label className="text-xs font-medium text-gray-700 mb-1">
                                Manager Email
                            </label>
                            <input
                                type="email"
                                value={newLocation.manager_email}
                                onChange={(e) => handleNewLocationChange('manager_email', e.target.value)}
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Enter email address"
                            />
                        </div>

                        {/* Operating Hours Section */}
                        <div className="md:col-span-2">
                            <h4 className="text-sm font-medium text-gray-900 mb-2 border-b pb-1">Operating Hours</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {Object.entries(newLocation.operating_hours).map(([day, hours]) => (
                                    <div key={day} className="flex items-center space-x-2">
                                        <label className="block text-xs font-medium text-gray-700 w-16">
                                            {day.charAt(0).toUpperCase() + day.slice(1)}
                                        </label>
                                        <input
                                            type="time"
                                            value={hours.open}
                                            onChange={(e) => handleOperatingHoursChange(day, 'open', e.target.value)}
                                            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        />
                                        <span className="text-xs text-gray-500">to</span>
                                        <input
                                            type="time"
                                            value={hours.close}
                                            onChange={(e) => handleOperatingHoursChange(day, 'close', e.target.value)}
                                            className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end space-x-3">
                        <button
                            onClick={() => setShowAddLocationRow(false)}
                            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddLocation}
                            disabled={loading}
                            className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 border border-blue-600 rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                        >
                            {loading ? 'Submitting...' : 'Submit Registration'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default React.memo(AddLocationRow); 